import type { AgentKind, Message } from '../agents/types';
import { useSessionStore } from '../store/sessionStore';
import { AGENT_REGISTRY } from '../agents/registry';
import { shortId } from '../utils';

export type CommandHandlerResult =
  | { kind: 'consumed' }                                  // command done, drop input
  | { kind: 'consumed_then_send'; remaining: string }     // command done, send remaining as user message
  | { kind: 'unknown' }                                   // not a recognized command
  | { kind: 'error'; message: string };

interface Ctx {
  attachFile?: () => void;     // wired by Composer
  attachFolder?: () => void;
}

/**
 * Run a slash command with the rest of the line as args.
 * Returns whether the line was consumed.
 */
export function runSlashCommand(name: string, args: string, ctx: Ctx = {}): CommandHandlerResult {
  const store = useSessionStore.getState();
  const activeId = store.activeId;
  const remainder = args.trim();

  switch (name) {
    case 'clear': {
      if (!activeId) return { kind: 'error', message: 'No active session.' };
      store.clearMessages(activeId);
      return { kind: 'consumed' };
    }

    case 'new-session': {
      const kind = (remainder as AgentKind) in AGENT_REGISTRY ? (remainder as AgentKind) : 'claude';
      store.newSession(kind);
      return { kind: 'consumed' };
    }

    case 'agent': {
      if (!activeId) return { kind: 'error', message: 'No active session.' };
      if (!(remainder in AGENT_REGISTRY)) {
        return { kind: 'error', message: `Unknown agent: ${remainder || '(none)'}` };
      }
      store.setAgentKind(activeId, remainder as AgentKind);
      return { kind: 'consumed' };
    }

    case 'model': {
      if (!activeId) return { kind: 'error', message: 'No active session.' };
      if (!remainder) return { kind: 'error', message: 'Usage: /model <id>' };
      store.setModel(activeId, remainder);
      return { kind: 'consumed' };
    }

    case 'tools': {
      if (!activeId) return { kind: 'error', message: 'No active session.' };
      const session = store.sessions.find((s) => s.id === activeId);
      const list = session?.availableTools.length
        ? session.availableTools.map((t) => `• ${t}`).join('\n')
        : '(no tools configured)';
      const sysMsg: Message = {
        id: shortId('msg'),
        role: 'system',
        createdAt: Date.now(),
        parts: [{ type: 'text', text: `Available tools:\n${list}` }],
      };
      store.appendMessage(activeId, sysMsg);
      return { kind: 'consumed' };
    }

    case 'attach': {
      ctx.attachFile?.();
      return { kind: 'consumed' };
    }

    case 'plan':
    case 'debug':
    case 'research':
    case 'code':
    case 'review': {
      // spawn a new session of this workflow kind, send the remainder as the first user message
      const kindMap: Record<string, AgentKind> = {
        plan: 'claude',
        debug: 'debug',
        research: 'research',
        code: 'codex',
        review: 'claude',
      };
      store.newSession(kindMap[name], `/${name} ${remainder.slice(0, 28) || 'session'}`);
      if (remainder) return { kind: 'consumed_then_send', remaining: remainder };
      return { kind: 'consumed' };
    }

    default:
      return { kind: 'unknown' };
  }
}

/**
 * Parse a textarea value. Returns the command + args if it begins with `/`.
 */
export function parseSlash(value: string): { name: string; args: string } | null {
  const trimmed = value.trimStart();
  if (!trimmed.startsWith('/')) return null;
  const space = trimmed.indexOf(' ');
  if (space === -1) {
    return { name: trimmed.slice(1), args: '' };
  }
  return {
    name: trimmed.slice(1, space),
    args: trimmed.slice(space + 1),
  };
}
